// /src/FollowUpPage.js
import React, { useState, useEffect } from "react";
import api from "./services/api";
import generatePDF from "./PDFGenerator";
import ReminderModal from "./ReminderModal";
import { Link } from "react-router-dom";

const FollowUpPage = () => {
  const [data, setData] = useState({ count: 0, totalValue: 0, quotations: [] });
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeDayFilter, setActiveDayFilter] = useState("all"); // "all" | "3to5" | "6to10" | "11to14"
  const [activeStatusFilter, setActiveStatusFilter] = useState("all"); // "all" | "never" | "reminded"
  const [searchTerm, setSearchTerm] = useState("");

  // Single reminder target
  const [singleTarget, setSingleTarget] = useState(null);

  // Bulk dispatch modal states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkLang, setBulkLang] = useState("both");
  const [bulkAttachPdf, setBulkAttachPdf] = useState(true);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentName: "", results: [] });

  const fetchFollowups = async () => {
    try {
      setLoading(true);
      const res = await api.get("/whatsapp/followups?minDays=3&maxDays=14");
      if (res.data) {
        setData(res.data);
        setSelectedIds((res.data.quotations || []).map((q) => q.id));
      }
    } catch (err) {
      console.error("Error fetching follow-ups:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowups();
  }, []);

  // Filtered quotations based on search, day filter, and status filter
  const filteredQuotations = (data.quotations || []).filter((q) => {
    // Search
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      const match =
        (q.customer_name || "").toLowerCase().includes(s) ||
        (q.quotation_no || "").toLowerCase().includes(s) ||
        (q.mobile || "").includes(s) ||
        (q.city || "").toLowerCase().includes(s);
      if (!match) return false;
    }

    // Day filter
    if (activeDayFilter === "3to5" && (q.days_ago < 3 || q.days_ago > 5)) return false;
    if (activeDayFilter === "6to10" && (q.days_ago < 6 || q.days_ago > 10)) return false;
    if (activeDayFilter === "11to14" && (q.days_ago < 11 || q.days_ago > 14)) return false;

    // Status filter
    const count = Number(q.reminder_count) || 0;
    if (activeStatusFilter === "never" && count > 0) return false;
    if (activeStatusFilter === "reminded" && count === 0) return false;

    return true;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredQuotations.map((q) => q.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Sequential Bulk Reminder Runner with safe 3-second throttle
  const handleStartBulkSending = async () => {
    const targets = (data.quotations || []).filter((q) => selectedIds.includes(q.id));
    if (targets.length === 0) return;

    setBulkSending(true);
    setBulkProgress({
      current: 0,
      total: targets.length,
      currentName: targets[0].customer_name,
      results: [],
    });

    const results = [];

    for (let i = 0; i < targets.length; i++) {
      const q = targets[i];
      setBulkProgress((prev) => ({
        ...prev,
        current: i + 1,
        currentName: q.customer_name,
      }));

      try {
        let pdfBase64 = null;
        if (bulkAttachPdf) {
          try {
            pdfBase64 = generatePDF({
              customer: {
                name: q.customer_name,
                city: q.city,
                date: q.date ? new Date(q.date).toLocaleDateString("en-GB") : "",
                mobile: q.mobile,
                quotationNo: q.quotation_no,
              },
              categoryNames: [],
              selectedItems: q.items || [],
              total: Number(q.total || 0),
              discountAmount: 0,
              grandTotal: Number(q.net_total || 0),
              includeGst: q.include_gst !== 0 && q.include_gst !== false,
            }, "base64");
          } catch (pdfErr) {
            console.warn("Could not generate PDF for bulk item:", pdfErr);
          }
        }

        await api.post("/whatsapp/send-reminder", {
          quotationId: q.id,
          mobile: q.mobile,
          quotationNo: q.quotation_no,
          customerName: q.customer_name,
          grandTotal: q.net_total,
          date: q.date ? new Date(q.date).toLocaleDateString("en-GB") : "",
          pdfBase64,
          language: bulkLang,
        });

        results.push({ id: q.id, name: q.customer_name, success: true });
      } catch (err) {
        console.error(`Bulk reminder error for ${q.customer_name}:`, err);
        results.push({
          id: q.id,
          name: q.customer_name,
          success: false,
          error: err.response?.data?.error || err.message,
        });
      }

      setBulkProgress((prev) => ({
        ...prev,
        results: [...results],
      }));

      // Safe anti-spam pause between messages (3 seconds), except after final message
      if (i < targets.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
    }

    setBulkSending(false);
    fetchFollowups();
  };

  const selectedCount = selectedIds.length;
  const neverRemindedCount = (data.quotations || []).filter((q) => Number(q.reminder_count) === 0).length;
  const alreadyRemindedCount = (data.quotations || []).filter((q) => Number(q.reminder_count) > 0).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white overflow-x-hidden pb-20">
      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="orb absolute w-[500px] h-[500px] -top-20 -left-20 bg-amber-600/15 rounded-full blur-[120px]"></div>
        <div className="orb absolute w-[400px] h-[400px] bottom-0 right-0 bg-purple-600/20 rounded-full blur-[100px]"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 relative z-10 max-w-7xl">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-white/10 pb-6">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎯</span>
              <h1 className="text-3xl md:text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-amber-200 via-purple-200 to-indigo-200">
                Today's Quotation Follow-Ups
              </h1>
            </div>
            <p className="text-purple-300/70 text-sm mt-1">
              Active quotations created 3 to 14 days ago. Follow up with customers via WhatsApp to close pending deals.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/quotations"
              className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-all flex items-center gap-2"
            >
              <i className="fas fa-arrow-left text-xs"></i>
              <span>All Quotations</span>
            </Link>

            <button
              onClick={() => setShowBulkModal(true)}
              disabled={selectedCount === 0}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 shadow-lg ${
                selectedCount > 0
                  ? "bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-green-950/40 cursor-pointer"
                  : "bg-white/10 text-white/30 cursor-not-allowed"
              }`}
            >
              <i className="fab fa-whatsapp text-base"></i>
              <span>⚡ Remind Selected ({selectedCount})</span>
            </button>
          </div>
        </div>

        {/* Top Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-panel p-5 rounded-2xl bg-white/5 border border-white/10">
            <span className="text-xs uppercase font-bold tracking-wider text-purple-300/60 block mb-1">
              Pending Follow-Ups
            </span>
            <span className="text-2xl md:text-3xl font-black font-mono text-white">
              {data.count}
            </span>
            <span className="text-[11px] text-white/40 block mt-1">Quotes 3–14 days old</span>
          </div>

          <div className="glass-panel p-5 rounded-2xl bg-white/5 border border-white/10">
            <span className="text-xs uppercase font-bold tracking-wider text-emerald-400/80 block mb-1">
              Potential Order Value
            </span>
            <span className="text-2xl md:text-3xl font-black font-mono text-emerald-400">
              ₹{Number(data.totalValue || 0).toLocaleString("en-IN")}
            </span>
            <span className="text-[11px] text-white/40 block mt-1">Total pending amount</span>
          </div>

          <div className="glass-panel p-5 rounded-2xl bg-white/5 border border-white/10">
            <span className="text-xs uppercase font-bold tracking-wider text-amber-400/80 block mb-1">
              Never Reminded
            </span>
            <span className="text-2xl md:text-3xl font-black font-mono text-amber-400">
              {neverRemindedCount}
            </span>
            <span className="text-[11px] text-white/40 block mt-1">High conversion priority</span>
          </div>

          <div className="glass-panel p-5 rounded-2xl bg-white/5 border border-white/10">
            <span className="text-xs uppercase font-bold tracking-wider text-cyan-400/80 block mb-1">
              Already Followed Up
            </span>
            <span className="text-2xl md:text-3xl font-black font-mono text-cyan-300">
              {alreadyRemindedCount}
            </span>
            <span className="text-[11px] text-white/40 block mt-1">Reminded 1 or more times</span>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="glass-panel p-5 rounded-2xl bg-white/5 border border-white/10 mb-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          {/* Search */}
          <div className="relative w-full lg:w-96">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-purple-300/50"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by customer, phone, city..."
              className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400/50 transition-colors"
            />
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
            <div className="flex bg-slate-900/60 p-1 rounded-xl border border-white/10 text-xs">
              <button
                onClick={() => setActiveDayFilter("all")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeDayFilter === "all" ? "bg-amber-500 text-slate-950 shadow" : "text-white/60 hover:text-white"
                }`}
              >
                All Days
              </button>
              <button
                onClick={() => setActiveDayFilter("3to5")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeDayFilter === "3to5" ? "bg-amber-500 text-slate-950 shadow" : "text-white/60 hover:text-white"
                }`}
              >
                3–5 Days
              </button>
              <button
                onClick={() => setActiveDayFilter("6to10")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeDayFilter === "6to10" ? "bg-amber-500 text-slate-950 shadow" : "text-white/60 hover:text-white"
                }`}
              >
                6–10 Days
              </button>
              <button
                onClick={() => setActiveDayFilter("11to14")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeDayFilter === "11to14" ? "bg-amber-500 text-slate-950 shadow" : "text-white/60 hover:text-white"
                }`}
              >
                11–14 Days
              </button>
            </div>

            <div className="flex bg-slate-900/60 p-1 rounded-xl border border-white/10 text-xs">
              <button
                onClick={() => setActiveStatusFilter("all")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeStatusFilter === "all" ? "bg-purple-600 text-white shadow" : "text-white/60 hover:text-white"
                }`}
              >
                All Status
              </button>
              <button
                onClick={() => setActiveStatusFilter("never")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeStatusFilter === "never" ? "bg-purple-600 text-white shadow" : "text-white/60 hover:text-white"
                }`}
              >
                Never Reminded
              </button>
              <button
                onClick={() => setActiveStatusFilter("reminded")}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  activeStatusFilter === "reminded" ? "bg-purple-600 text-white shadow" : "text-white/60 hover:text-white"
                }`}
              >
                Reminded 1x+
              </button>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="glass-panel rounded-3xl overflow-hidden border border-white/10 shadow-xl bg-slate-900/40 backdrop-blur-md">
          {loading ? (
            <div className="p-16 text-center">
              <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-400 rounded-full animate-spin mx-auto mb-3"></div>
              <span className="text-purple-300/60 text-sm font-medium">Scanning quotations for follow-up candidates...</span>
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="p-16 text-center text-white/40 italic">
              <i className="fas fa-check-circle text-4xl text-emerald-400/50 mb-3 block"></i>
              No quotations match your current follow-up filter. You're all caught up!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-white/5 text-purple-200 text-xs uppercase tracking-wider font-bold border-b border-white/10">
                    <th className="py-4 px-4 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={
                          filteredQuotations.length > 0 &&
                          filteredQuotations.every((q) => selectedIds.includes(q.id))
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-white/20 text-amber-500 focus:ring-amber-400 bg-slate-900 cursor-pointer"
                      />
                    </th>
                    <th className="py-4 px-4">Quotation #</th>
                    <th className="py-4 px-4">Customer & City</th>
                    <th className="py-4 px-4">Age</th>
                    <th className="py-4 px-4">Net Amount</th>
                    <th className="py-4 px-4">Reminder Status</th>
                    <th className="py-4 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm">
                  {filteredQuotations.map((q) => {
                    const isSelected = selectedIds.includes(q.id);
                    const remCount = Number(q.reminder_count) || 0;
                    return (
                      <tr
                        key={q.id}
                        className={`hover:bg-white/5 transition-colors ${
                          isSelected ? "bg-amber-500/5" : ""
                        }`}
                      >
                        <td className="py-4 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(q.id)}
                            className="w-4 h-4 rounded border-white/20 text-amber-500 focus:ring-amber-400 bg-slate-900 cursor-pointer"
                          />
                        </td>
                        <td className="py-4 px-4 font-mono font-bold text-purple-300">
                          <span className="bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 rounded-lg">
                            {q.quotation_no}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-bold text-white text-base">{q.customer_name}</div>
                          <div className="text-xs text-white/50 flex items-center gap-2 mt-0.5">
                            <span className="font-mono">{q.mobile}</span>
                            {q.city && <span>• <i className="fas fa-map-marker-alt text-[10px]"></i> {q.city}</span>}
                          </div>
                        </td>
                        <td className="py-4 px-4 font-mono">
                          <span className="bg-amber-500/10 border border-amber-500/20 text-amber-300 px-2.5 py-1 rounded-full text-xs font-semibold">
                            🕒 {q.days_ago} days ago
                          </span>
                          <span className="block text-[11px] text-white/30 mt-0.5">
                            {q.date ? new Date(q.date).toLocaleDateString("en-GB") : ""}
                          </span>
                        </td>
                        <td className="py-4 px-4 font-mono font-bold text-emerald-400 text-base">
                          ₹{Number(q.net_total || 0).toLocaleString("en-IN")}
                        </td>
                        <td className="py-4 px-4">
                          {remCount > 0 ? (
                            <div>
                              <span className="bg-amber-500/20 border border-amber-500/30 text-amber-300 px-2.5 py-1 rounded-full text-xs font-bold inline-block">
                                Reminded {remCount} time{remCount > 1 ? "s" : ""}
                              </span>
                              {q.last_reminder_date && (
                                <span className="block text-[11px] text-white/40 mt-1">
                                  Last: {new Date(q.last_reminder_date).toLocaleDateString("en-GB")}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="bg-white/5 border border-white/10 text-white/50 px-2.5 py-1 rounded-full text-xs font-medium inline-block">
                              Never Reminded
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => setSingleTarget(q)}
                            className="px-4 py-2 rounded-xl font-bold text-xs bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-md shadow-green-950/30 transition-all inline-flex items-center gap-1.5"
                            title="Send WhatsApp reminder"
                          >
                            <i className="fab fa-whatsapp"></i>
                            <span>Send Reminder</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Single Reminder Modal */}
      {singleTarget && (
        <ReminderModal
          quotation={singleTarget}
          onClose={() => setSingleTarget(null)}
          onSuccess={(updated) => {
            setData((prev) => ({
              ...prev,
              quotations: prev.quotations.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item
              ),
            }));
          }}
        />
      )}

      {/* Bulk Dispatch Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="relative w-full max-w-lg rounded-3xl bg-slate-900 border border-amber-500/30 shadow-2xl p-6 md:p-8 flex flex-col gap-5 text-white">
            <button
              onClick={() => {
                if (!bulkSending) setShowBulkModal(false);
              }}
              disabled={bulkSending}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white"
            >
              <i className="fas fa-times"></i>
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#25D366]/20 border border-[#25D366]/30 flex items-center justify-center text-[#25D366] text-xl flex-shrink-0">
                <i className="fab fa-whatsapp"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {bulkSending ? "Sending Follow-Ups..." : "Bulk Follow-Up Dispatch"}
                </h3>
                <p className="text-xs text-purple-200/70">
                  {bulkSending
                    ? `Dispatching to ${bulkProgress.total} selected customers`
                    : `Send WhatsApp reminders to ${selectedCount} selected customer(s)`}
                </p>
              </div>
            </div>

            {!bulkSending && (
              <>
                {/* Language Select */}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-purple-300/70 block mb-2">
                    Reminder Language
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setBulkLang("both")}
                      className={`py-2 px-2.5 rounded-xl font-medium text-xs border text-center transition-all ${
                        bulkLang === "both"
                          ? "bg-purple-600 border-purple-400 text-white"
                          : "bg-white/5 border-white/10 text-white/70"
                      }`}
                    >
                      🌐 ગુજરાતી + Eng
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkLang("hi_en")}
                      className={`py-2 px-2.5 rounded-xl font-medium text-xs border text-center transition-all ${
                        bulkLang === "hi_en"
                          ? "bg-purple-600 border-purple-400 text-white"
                          : "bg-white/5 border-white/10 text-white/70"
                      }`}
                    >
                      🌐 हिंदी + Eng
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkLang("gu")}
                      className={`py-2 px-2.5 rounded-xl font-medium text-xs border text-center transition-all ${
                        bulkLang === "gu"
                          ? "bg-purple-600 border-purple-400 text-white"
                          : "bg-white/5 border-white/10 text-white/70"
                      }`}
                    >
                      🇮🇳 ગુજરાતી
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkLang("hi")}
                      className={`py-2 px-2.5 rounded-xl font-medium text-xs border text-center transition-all ${
                        bulkLang === "hi"
                          ? "bg-purple-600 border-purple-400 text-white"
                          : "bg-white/5 border-white/10 text-white/70"
                      }`}
                    >
                      🇮🇳 हिंदी
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkLang("en")}
                      className={`py-2 px-2.5 rounded-xl font-medium text-xs border text-center transition-all ${
                        bulkLang === "en"
                          ? "bg-purple-600 border-purple-400 text-white"
                          : "bg-white/5 border-white/10 text-white/70"
                      }`}
                    >
                      🇬🇧 English
                    </button>
                  </div>
                </div>

                {/* PDF Checkbox */}
                <div className="flex items-center gap-3 bg-white/5 p-3.5 rounded-2xl border border-white/5">
                  <input
                    id="bulkPdfCheckbox"
                    type="checkbox"
                    checked={bulkAttachPdf}
                    onChange={(e) => setBulkAttachPdf(e.target.checked)}
                    className="w-5 h-5 rounded border-white/20 text-purple-600 focus:ring-purple-500 bg-slate-900 cursor-pointer"
                  />
                  <label htmlFor="bulkPdfCheckbox" className="text-sm cursor-pointer select-none text-white/90">
                    <span className="font-semibold block">📎 Attach Quotation PDF to each message</span>
                    <span className="text-xs text-white/50 block">Generates official PDF document for each customer automatically.</span>
                  </label>
                </div>

                {/* Anti-Spam Notice */}
                <div className="bg-amber-500/10 border border-amber-500/20 p-3.5 rounded-2xl text-xs text-amber-200/80 flex items-center gap-2.5">
                  <i className="fas fa-shield-alt text-amber-400 text-base flex-shrink-0"></i>
                  <span>Messages are dispatched sequentially with a safe 3-second delay to protect your WhatsApp account.</span>
                </div>
              </>
            )}

            {/* Live Progress Bar During Dispatch */}
            {bulkSending && (
              <div className="space-y-4 py-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-amber-300 font-bold flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                    Sending to {bulkProgress.currentName}...
                  </span>
                  <span className="font-mono text-white/60">
                    {bulkProgress.current} of {bulkProgress.total}
                  </span>
                </div>

                <div className="w-full bg-slate-950 rounded-full h-3 overflow-hidden border border-white/10 p-0.5">
                  <div
                    className="bg-gradient-to-r from-amber-400 to-[#25D366] h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                    }}
                  ></div>
                </div>

                {/* Results list */}
                <div className="max-h-40 overflow-y-auto space-y-1.5 custom-scrollbar text-xs">
                  {bulkProgress.results.map((res, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 rounded-lg bg-white/5 text-[11px]"
                    >
                      <span className="text-white font-medium">{res.name}</span>
                      <span className={res.success ? "text-emerald-400" : "text-red-400"}>
                        {res.success ? "✅ Delivered" : `❌ ${res.error || "Failed"}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed state */}
            {!bulkSending && bulkProgress.total > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl text-xs text-emerald-300 flex items-center gap-3">
                <i className="fas fa-check-circle text-lg"></i>
                <div>
                  <span className="font-bold block">Follow-Up Batch Completed!</span>
                  <span className="text-white/70">
                    {bulkProgress.results.filter((r) => r.success).length} of {bulkProgress.total} messages sent successfully.
                  </span>
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                disabled={bulkSending}
                className="px-5 py-2.5 rounded-xl font-bold text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"
              >
                {bulkProgress.total > 0 && !bulkSending ? "Close" : "Cancel"}
              </button>

              {!bulkSending && (bulkProgress.total === 0 || bulkProgress.current === 0) && (
                <button
                  type="button"
                  onClick={handleStartBulkSending}
                  className="px-6 py-2.5 rounded-xl font-bold text-xs bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-green-900/30 flex items-center gap-2"
                >
                  <i className="fab fa-whatsapp"></i>
                  <span>Start Sending Reminders</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpPage;
